import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * 🔔 Follow / Following toggle for a category (optionally scoped to region).
 * Backend: POST/GET/DELETE /api/classifieds/follows
 * See spec: GET /api/agent-docs/WEB_MOBILE_SPEC_FOLLOWS_ALERTS
 */
export default function FollowCategoryButton({ categorySlug, categoryName, region }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();

  const followsQ = useQuery({
    queryKey: ["my-follows", user?.id],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/follows");
      return data?.follows || [];
    },
    enabled: !!user,
    staleTime: 60_000,
    retry: 0,
  });

  const existing = useMemo(() => {
    return (followsQ.data || []).find(
      (f) => f.category_slug === categorySlug && (f.region || null) === (region || null),
    );
  }, [followsQ.data, categorySlug, region]);

  const followMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post("/classifieds/follows", {
        category_slug: categorySlug,
        region: region || null,
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-follows", user?.id] });
      toast.success(`You'll get an email when new ads appear in ${categoryName || categorySlug}.`);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Could not follow — please try again."),
  });

  const unfollowMut = useMutation({
    mutationFn: async (id) => sharedApi.delete(`/classifieds/follows/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-follows", user?.id] });
      toast.success(`Alerts stopped for ${categoryName || categorySlug}.`);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Could not unfollow — please try again."),
  });

  const busy = followMut.isPending || unfollowMut.isPending || (user && followsQ.isLoading);
  const statusUnknown = !!user && followsQ.isError;
  const isFollowing = !!existing;

  const onClick = () => {
    if (!user) {
      const to = `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      nav(to);
      return;
    }
    if (statusUnknown) {
      followsQ.refetch();
      return;
    }
    if (existing) unfollowMut.mutate(existing.id);
    else followMut.mutate();
  };

  if (statusUnknown) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={`follow-category-${categorySlug}`}
        aria-pressed={false}
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-white text-slate-400 border border-soft hover:text-[color:var(--brand-blue)]"
        title="Couldn't load follow status — click to retry"
      >
        <Bell className="w-4 h-4" /> Retry
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={`follow-category-${categorySlug}`}
      aria-pressed={isFollowing}
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-70 ${
        isFollowing
          ? "bg-[color:var(--brand-blue)] text-white hover:bg-[color:var(--brand-blue-hover)]"
          : "bg-white text-slate-700 border border-soft hover:border-[color:var(--brand-blue)] hover:text-[color:var(--brand-blue)]"
      }`}
      title={isFollowing ? "You'll get emails when new ads go live" : "Get emailed when new ads go live"}
    >
      {busy ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isFollowing ? (
        <BellRing className="w-4 h-4" />
      ) : (
        <Bell className="w-4 h-4" />
      )}
      {isFollowing ? "Following" : "Follow"}
    </button>
  );
}
